import type {
  StudentMeetingDetails,
  StudentMeetingDetailsDto,
  StudentMeetingPeriod,
  StudentMeetingPeriodDto,
  SharedFeedbackScoreDto,
  StudentMeetingProgress,
  StudentMeetingProgressDto,
  StudentMeetingRosterStudent,
  StudentMeetingRosterStudentDto,
  TeacherScoreDto,
} from "meetings/student-meeting/types";
import type { TeacherScore } from "meetings/shared/types";
import { toDate } from "shared/mappers/coerce";

const teacherScoreFromDto = (dto: TeacherScoreDto): TeacherScore => ({
  teacherShortage: dto.teacher_shortage,
  total: dto.total,
  filled: dto.filled,
  percentage: dto.total > 0 ? Math.round((dto.filled / dto.total) * 100) : 0,
});

// Shared-report counts rendered by the same progress-list component, so they
// project onto the TeacherScore shape (shared -> filled).
const sharedFeedbackScoreFromDto = (
  dto: SharedFeedbackScoreDto,
): TeacherScore =>
  teacherScoreFromDto({
    teacher_shortage: dto.teacher_shortage,
    total: dto.total,
    filled: dto.shared,
  });

export const studentMeetingProgressFromDto = (
  dto: StudentMeetingProgressDto,
): StudentMeetingProgress => ({
  id: dto.id,
  name: dto.name,
  status: dto.status,
  totalRatings: dto.total_ratings,
  totalGivenRatings: dto.total_given_ratings,
  teacherScores: dto.teacher_scores.map(teacherScoreFromDto),
  reportsSharedUnique: dto.reports_shared_unique,
  totalReportsSharedUnique: dto.total_reports_shared_unique,
  sharedFeedbackScores: (dto.shared_feedback ?? []).map(
    sharedFeedbackScoreFromDto,
  ),
  allowGrades: dto.allow_grades === 1,
  mailStudent: dto.mail_student === 1,
  schoolIsDemo: dto.school_is_demo === 1,
});

export const rosterStudentFromDto = (
  dto: StudentMeetingRosterStudentDto,
): StudentMeetingRosterStudent => ({
  id: dto.id,
  name: dto.name,
  mentorClass: dto.mentor_class ?? "",
});

export const periodsFromDto = (
  dtos: StudentMeetingPeriodDto[] | undefined,
): StudentMeetingPeriod[] =>
  (dtos ?? []).map((dto) => ({
    from: toDate(dto.from),
    till: toDate(dto.till),
  }));

export const studentMeetingDetailsFromDto = (
  dto: StudentMeetingDetailsDto,
): StudentMeetingDetails => ({
  id: dto.id,
  name: dto.name,
  status: dto.status,
  message: dto.message ?? "",
  mailStudent: dto.mail_student === 1,
  allowStudentRatings: dto.allow_student_ratings === 1,
  allowGrades: dto.allow_grades === 1,
  mentorClasses: [...(dto.mentor_classes ?? [])],
  subjects: [...(dto.subjects ?? [])],
  opensAt: toDate(dto.timeframe_settings?.opens_at),
  closedModifyAt: toDate(dto.timeframe_settings?.closed_modify_at),
  closedNoModifyAt: toDate(dto.timeframe_settings?.closed_no_modify_at),
  shareFeedbackAt: toDate(dto.timeframe_settings?.share_feedback_at),
  archivedAt: toDate(dto.timeframe_settings?.archived_at),
  triangleFrom: toDate(dto.timeframe_settings?.triangle_conversation_from),
  triangleTill: toDate(dto.timeframe_settings?.triangle_conversation_till),
  feedbackReservations: periodsFromDto(
    dto.timeframe_settings?.feedback_reservations,
  ),
  speedDatePreparations: periodsFromDto(
    dto.timeframe_settings?.speed_date_preparations,
  ),
  speedDates: periodsFromDto(dto.timeframe_settings?.speed_dates),
});
